import {
  ICON_CATEGORY_LABELS,
  ICON_WEIGHT_LABELS,
  ICON_WEIGHT_TITLES,
  ICON_WEIGHTS,
  type IconChunkLayer,
  type IconElement,
  type IconWeight,
  iconLabel,
  loadIconCatalog,
  loadIconWeight,
  searchIcons,
} from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { FlipHorizontal2, FlipVertical2, Replace, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Input, Select } from "../components/ui/input";
import { cn } from "../lib/utils";
import { type BrandLink, ColorButton } from "./ColorPicker";
import { addIcon, ICON_DRAG_TYPE, replaceIcon, setIconWeight } from "./commands";
import { useEditorApi } from "./store";
import { IconButton, Row, Section, Segmented } from "./ui";

/**
 * Icon library (Phosphor): browse and search in the elements panel, click or
 * drag onto the canvas; style, colour and swapping in the properties panel.
 * The catalog is loaded on demand, the first time an icon list is shown.
 */

const PAGE_SIZE = 60;

/** Style chosen in the elements panel, kept while the editor is open. */
let lastWeight: IconWeight = "regular";

function useIconCatalog() {
  return useQuery({
    queryKey: ["icon-catalog"],
    queryFn: loadIconCatalog,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });
}

function useIconChunk(weight: IconWeight) {
  return useQuery({
    queryKey: ["icon-catalog", weight],
    queryFn: () => loadIconWeight(weight),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });
}

function IconGlyph({ layers }: { layers: IconChunkLayer[] }) {
  return (
    <svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" className="size-full">
      {layers.map((layer, index) =>
        typeof layer === "string" ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: layers have no identity of their own
          <path key={index} d={layer} />
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: layers have no identity of their own
          <path key={index} d={layer[0]} opacity={layer[1]} />
        ),
      )}
    </svg>
  );
}

export function WeightSwitch({
  value,
  onChange,
}: {
  value: IconWeight | undefined;
  onChange: (weight: IconWeight) => void;
}) {
  return (
    <Segmented
      className="grid w-full grid-cols-3"
      value={value}
      onChange={onChange}
      options={ICON_WEIGHTS.map((weight) => ({
        value: weight,
        label: ICON_WEIGHT_LABELS[weight],
        title: `Stil: ${ICON_WEIGHT_TITLES[weight]}`,
      }))}
    />
  );
}

/** Search field, category and icon grid. Tiles can be clicked or dragged onto the canvas. */
export function IconBrowser({
  weight,
  onPick,
  draggable = false,
  autoFocus = false,
  initialQuery = "",
  preview,
}: {
  weight: IconWeight;
  onPick: (name: string) => void;
  draggable?: boolean;
  autoFocus?: boolean;
  /** Search to start with (the elements panel's search). */
  initialQuery?: string;
  /** Only the first results for `initialQuery`, without search field and paging. */
  preview?: number;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState("");
  const [shown, setShown] = useState(PAGE_SIZE);
  const catalog = useIconCatalog();
  const chunk = useIconChunk(weight);
  const results = useMemo(
    () => (catalog.data ? searchIcons(catalog.data, query, { category }) : []),
    [catalog.data, query, category],
  );
  const categories = useMemo(
    () =>
      (catalog.data?.categories ?? [])
        .map((key) => ({ key, label: ICON_CATEGORY_LABELS[key] ?? key }))
        .sort((a, b) => a.label.localeCompare(b.label, "de")),
    [catalog.data],
  );
  const failed = catalog.isError || chunk.isError;
  return (
    <div className="space-y-2">
      <div className={cn("relative", preview && "hidden")}>
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-zinc-400" />
        <Input
          autoFocus={autoFocus}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setShown(PAGE_SIZE);
          }}
          placeholder="Suchen, z. B. Herz, Pfeil"
          aria-label="Icons suchen"
          className="h-9 pr-8 pl-8 text-sm"
        />
        {query ? (
          <button
            type="button"
            title="Suche leeren"
            onClick={() => setQuery("")}
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-zinc-400 hover:text-zinc-700"
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>
      <Select
        className={cn("h-8 text-xs", preview && "hidden")}
        aria-label="Kategorie"
        value={category}
        onChange={(event) => {
          setCategory(event.target.value);
          setShown(PAGE_SIZE);
        }}
      >
        <option value="">{query.trim() ? "Alle Kategorien" : "Beliebt"}</option>
        {categories.map((entry) => (
          <option key={entry.key} value={entry.key}>
            {entry.label}
          </option>
        ))}
      </Select>
      {failed ? (
        <p className="text-xs text-red-600">
          Die Icons konnten nicht geladen werden.{" "}
          <button
            type="button"
            className="font-medium underline"
            onClick={() => {
              void catalog.refetch();
              void chunk.refetch();
            }}
          >
            Erneut versuchen
          </button>
        </p>
      ) : !catalog.data || !chunk.data ? (
        <div className="grid grid-cols-5 gap-1.5" aria-busy="true">
          {Array.from({ length: 15 }, (_, index) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: placeholders
              key={index}
              className="aspect-square animate-pulse rounded-lg bg-zinc-100"
            />
          ))}
        </div>
      ) : results.length === 0 ? (
        <p className="py-2 text-xs text-zinc-500">
          Kein Icon gefunden. Versuche einen anderen Begriff – englische Begriffe gehen auch.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-5 gap-1.5">
            {results.slice(0, preview ?? shown).map((icon) => {
              const layers = chunk.data[icon.name];
              if (!layers) return null;
              return (
                <button
                  key={icon.name}
                  type="button"
                  title={iconLabel(icon.name)}
                  aria-label={`Icon ${iconLabel(icon.name)}`}
                  draggable={draggable}
                  onDragStart={(event) => {
                    event.dataTransfer.setData(
                      ICON_DRAG_TYPE,
                      JSON.stringify({ name: icon.name, weight }),
                    );
                    event.dataTransfer.effectAllowed = "copy";
                  }}
                  onClick={() => onPick(icon.name)}
                  className="flex aspect-square items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 p-2 text-zinc-700 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                >
                  <IconGlyph layers={layers} />
                </button>
              );
            })}
          </div>
          {!preview && results.length > shown ? (
            <button
              type="button"
              onClick={() => setShown(shown + PAGE_SIZE * 2)}
              className="w-full rounded-lg py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50"
            >
              Mehr anzeigen ({results.length - shown} weitere)
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

/** "Icons" section of the elements panel. */
export function IconInsert({ query = "" }: { query?: string }) {
  const store = useEditorApi();
  const [weight, setWeight] = useState<IconWeight>(lastWeight);
  return (
    <div className="space-y-2">
      <WeightSwitch
        value={weight}
        onChange={(next) => {
          lastWeight = next;
          setWeight(next);
        }}
      />
      <IconBrowser
        key={query}
        weight={weight}
        draggable
        initialQuery={query}
        onPick={(name) => void addIcon(store, name, weight)}
      />
      <p className="text-[11px] text-zinc-500">
        Klicken oder auf die Seite ziehen. Icons von{" "}
        <a
          href="https://phosphoricons.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-zinc-700"
        >
          Phosphor
        </a>{" "}
        (MIT-Lizenz).
      </p>
    </div>
  );
}

/** Properties of selected icons: colour, style, swap and mirroring. */
export function IconProperties({
  elements,
  colorLink,
}: {
  elements: IconElement[];
  colorLink?: BrandLink | undefined;
}) {
  const store = useEditorApi();
  const [swapping, setSwapping] = useState(false);
  const first = elements[0]!;
  const single = elements.length === 1 ? first : null;
  const ids = elements.map((element) => element.id);
  const weight = elements.every((element) => element.weight === first.weight)
    ? first.weight
    : undefined;
  const update = (recipe: (target: IconElement) => void) =>
    store.getState().update((draft) => {
      for (const id of ids) {
        const target = draft.elements[id];
        if (target?.type === "icon") recipe(target);
      }
    });
  return (
    <Section title="Icon">
      {single ? (
        <p className="-mt-1 truncate text-xs text-zinc-500">{iconLabel(single.icon)}</p>
      ) : null}
      <Row>
        <span className="w-20 shrink-0 text-xs text-zinc-500">Farbe</span>
        <ColorButton
          value={first.color}
          onChange={(color) => update((target) => (target.color = color))}
          brandLink={colorLink}
        />
      </Row>
      <WeightSwitch value={weight} onChange={(next) => void setIconWeight(store, ids, next)} />
      <Row>
        {single ? (
          <button
            type="button"
            onClick={() => setSwapping(!swapping)}
            aria-expanded={swapping}
            className={cn(
              "inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border text-xs font-medium [&_svg]:size-3.5",
              swapping
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-zinc-200 text-zinc-700 hover:bg-zinc-50",
            )}
          >
            <Replace /> Icon tauschen
          </button>
        ) : (
          <span className="flex-1" />
        )}
        <IconButton
          label="Horizontal spiegeln"
          onClick={() => update((target) => (target.flipX = !target.flipX))}
        >
          <FlipHorizontal2 />
        </IconButton>
        <IconButton
          label="Vertikal spiegeln"
          onClick={() => update((target) => (target.flipY = !target.flipY))}
        >
          <FlipVertical2 />
        </IconButton>
      </Row>
      {single && swapping ? (
        <IconBrowser
          weight={single.weight}
          autoFocus
          onPick={(name) => {
            void replaceIcon(store, single.id, name);
            setSwapping(false);
          }}
        />
      ) : null}
    </Section>
  );
}

/** First icons for a search of the elements panel (style as last picked). */
export function IconSearchPreview({ query }: { query: string }) {
  const store = useEditorApi();
  return (
    <IconBrowser
      key={query}
      weight={lastWeight}
      draggable
      initialQuery={query}
      preview={10}
      onPick={(name) => void addIcon(store, name, lastWeight)}
    />
  );
}
